import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
    organizationProductsMinimumAllowedPriceKeys,
    putOrganizationProductsMinimumAllowedPrices,
} from 'api/Cerebro/orgScoped/organizationProductsMinimumAllowedPriceApi'
import { distributionAnalysisKeys } from 'dynamicModules/DistributionAnalysis/api/distributionAnalysisApi'
import { useCerebroApiRequest } from 'hooks'
import message from 'utilities/message'

interface UpdateMapPriceArgs {
    productId: string
    currency: string
    mapPrice: number | null
    onSuccess?: () => void
}

interface UseUpdateMapPriceResult {
    updateMapPrice: (
        args: UpdateMapPriceArgs
    ) => Promise<{ status: number } | null>
}

// Backend treats null as "delete this row"; an empty InputNumber parses to 0.
const normalizeMapPrice = (value: number | null): number | null =>
    value === 0 ? null : value

export function useUpdateMapPrice(): UseUpdateMapPriceResult {
    const { t } = useTranslation()
    const makeCerebroApiRequest = useCerebroApiRequest()
    const queryClient = useQueryClient()

    const updateMapPrice = ({
        productId,
        currency,
        mapPrice,
        onSuccess,
    }: UpdateMapPriceArgs): Promise<{ status: number } | null> =>
        makeCerebroApiRequest({
            request: putOrganizationProductsMinimumAllowedPrices({
                items: [
                    {
                        product_id: productId,
                        currency,
                        map_price: normalizeMapPrice(mapPrice),
                    },
                ],
            }),
            onRequestSuccess: () => {
                message.success(
                    t('productCatalog:mapInlineEdit.success', 'MAP updated')
                )
                queryClient.invalidateQueries({
                    queryKey: organizationProductsMinimumAllowedPriceKeys.all,
                })
                queryClient.invalidateQueries({
                    queryKey: [
                        ...distributionAnalysisKeys.all,
                        'mapViolations',
                    ],
                })
                onSuccess?.()
            },
            onRequestFailure: () => {
                message.error(
                    t(
                        'productCatalog:mapInlineEdit.error',
                        'Failed to update MAP'
                    )
                )
            },
        })

    return { updateMapPrice }
}
